import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { Pill, StateCard } from '../../components/StateCard';
import { SiteHead, dataRequestStateLabel, formatDate } from '../../components/site';
import type { ApiEnvelope } from '../../lib/types';

interface RequestResult { reference: string; state: string; next_step: string; note: string }

interface LookupResult {
  reference: string; kind: string; state: string;
  retention_holds: { reason: string; until?: string }[];
  download_expires_at: string | null;
  completed_at: string | null;
}

const KINDS = [
  { value: 'export', label: '내보내기', description: '내 데이터 파일 받기' },
  { value: 'access', label: '열람', description: '보관 중인 데이터 확인' },
  { value: 'rectify', label: '정정', description: '잘못된 데이터 수정 요청' },
  { value: 'delete', label: '삭제', description: '내 데이터 삭제 요청' },
];

/** P015 — data-rights request. Identity is verified before anything is processed. */
export function PublicPrivacyRequests() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<RequestResult | null>(null);
  const [lookup, setLookup] = useState<{ reference: string; email: string } | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const games = useResource<{ id: string; slug: string; name: string }[]>(
    (signal) => api.public('/api/v1/games', { signal, query: { per_page: 60 } }) as Promise<ApiEnvelope<{ id: string; slug: string; name: string }[]>>,
    [],
  );

  const status = useResource<LookupResult>(
    (signal) => {
      if (!lookup) return Promise.reject(new ApiError(0, 'IDLE', '조회 전', '-'));
      return api.public(`/api/v1/privacy/requests/${lookup.reference}`, {
        signal,
        query: { contact_email: lookup.email },
      }) as Promise<ApiEnvelope<LookupResult>>;
    },
    [lookup?.reference, lookup?.email],
    { emptyWhen: () => false },
  );

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSubmitting(true);
    setError(null);

    try {
      const response = await api.public('/api/v1/privacy/requests', {
        method: 'POST',
        body: {
          kind: form.get('kind'),
          contact_email: form.get('contact_email'),
          all_games: form.get('all_games') === 'on',
          game_ids: form.getAll('game_ids').map(String),
        },
      }) as ApiEnvelope<RequestResult>;
      setResult(response.data);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause : new ApiError(0, 'UNKNOWN', '요청 실패', '-'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <SiteHead
        eyebrow="내 데이터"
        title="내 데이터 요청"
        lead="열람·정정·내보내기·삭제 · 본인 확인 필요"
      />

      {result ? (
        <div className="card" style={{ marginBottom: 36 }}>
          <h2 className="section__title" style={{ marginBottom: 8 }}>요청 접수 완료</h2>
          <dl className="spec-list">
            <dt>접수번호</dt><dd>{result.reference}</dd>
            <dt>상태</dt><dd>{dataRequestStateLabel(result.state)}</dd>
            <dt>다음 단계</dt><dd>본인 확인</dd>
          </dl>
          <p style={{ margin: '16px 0 0', lineHeight: 1.8, color: 'var(--text-secondary)' }}>{result.note}</p>
        </div>
      ) : (
        <>
          {error && (
            <div className="banner" style={{ marginBottom: 24 }}>
              <span aria-hidden="true">!</span>
              <div>{error.message}</div>
            </div>
          )}

          <form className="form-card" onSubmit={submit} style={{ marginBottom: 40 }}>
            <fieldset style={{ border: 0, padding: 0, margin: '0 0 8px' }}>
              <legend style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>요청 종류</legend>
              {KINDS.map((kind, index) => (
                <label key={kind.value} className="form-check" style={{ alignItems: 'flex-start' }}>
                  <input type="radio" name="kind" value={kind.value} defaultChecked={index === 0} required />
                  <span>
                    <strong>{kind.label}</strong>
                    <br />
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>{kind.description}</span>
                  </span>
                </label>
              ))}
            </fieldset>

            <label htmlFor="privacy-email">연락받을 이메일</label>
            <input id="privacy-email" name="contact_email" type="email" required />

            <label className="form-check">
              <input type="checkbox" name="all_games" />
              <span>모든 게임에 대해 요청</span>
            </label>

            {(games.data?.length ?? 0) > 0 && (
              <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
                <legend style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>특정 게임만 선택</legend>
                {games.data!.map((game) => (
                  <label key={game.id} className="form-check">
                    <input type="checkbox" name="game_ids" value={game.id} />
                    <span>{game.name}</span>
                  </label>
                ))}
              </fieldset>
            )}

            <div className="banner banner--info">
              <span aria-hidden="true">ℹ</span>
              <div>결제 기록 등 법정 보존 항목은 삭제 요청 후에도 보존 기간까지 유지</div>
            </div>

            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? '접수 중…' : '요청 접수'}
            </button>
          </form>
        </>
      )}

      <section className="card">
        <h2 className="section__title" style={{ marginBottom: 12 }}>처리 상태 조회</h2>
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            setLookupError(null);
            setLookup({ reference: String(form.get('reference')), email: String(form.get('email')) });
          }}
        >
          <div>
            <label htmlFor="lookup-ref">접수번호</label>
            <input id="lookup-ref" name="reference" required />
          </div>
          <div>
            <label htmlFor="lookup-email">접수 시 입력한 이메일</label>
            <input id="lookup-email" name="email" type="email" required />
          </div>
          <div style={{ alignSelf: 'end' }}>
            <button type="submit" className="btn">조회</button>
          </div>
        </form>

        {lookupError && <p style={{ color: 'var(--danger)' }}>{lookupError}</p>}

        {lookup && (
          <div style={{ marginTop: 20 }}>
            <StateCard state={status.state} error={status.error} onRetry={status.reload}>
              {status.data && (
                <dl className="spec-list">
                  <dt>접수번호</dt><dd>{status.data.reference}</dd>
                  <dt>종류</dt><dd>{KINDS.find((k) => k.value === status.data!.kind)?.label ?? status.data.kind}</dd>
                  <dt>상태</dt><dd><Pill tone={status.data.state === 'COMPLETED' ? 'ok' : 'info'}>{dataRequestStateLabel(status.data.state)}</Pill></dd>
                  <dt>보존 의무</dt>
                  <dd>
                    {status.data.retention_holds.length === 0
                      ? '해당 없음'
                      : status.data.retention_holds.map((hold, index) => (
                        <div key={index}>{hold.reason}{hold.until ? ` (${formatDate(hold.until)}까지)` : ''}</div>
                      ))}
                  </dd>
                  <dt>내려받기 만료</dt>
                  <dd>{status.data.download_expires_at ? formatDate(status.data.download_expires_at, true) : '해당 없음'}</dd>
                  <dt>완료</dt>
                  <dd>{status.data.completed_at ? formatDate(status.data.completed_at, true) : '처리 중'}</dd>
                </dl>
              )}
            </StateCard>
          </div>
        )}
      </section>

      <div style={{ marginTop: 28 }}>
        <Link className="btn" to="/legal/privacy">개인정보처리방침 보기</Link>
      </div>
    </>
  );
}
